import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import LeadForm from "./LeadForm";

const STORAGE_KEY = "si_exit_popup_dismissed";
const SESSION_KEY = "si_exit_popup_session_dismissed";

/**
 * Exit-intent popup — triggers when the cursor leaves the viewport (desktop)
 * OR after 30s + 50% scroll (mobile + desktop).
 *
 * Dismissal contract (fixes "popup keeps coming back" bug):
 *   - Clicking the close X, the overlay, or submitting the form sets
 *     localStorage (7-day cooldown) AND sessionStorage (current tab) AND
 *     an in-memory ref so the popup can NEVER re-trigger within the same
 *     mount, even on page navigation across routes.
 */
export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const dismissedRef = useRef(false);

  // Read persisted dismissal up-front so we never even arm timers.
  if (typeof window !== "undefined" && !dismissedRef.current) {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") dismissedRef.current = true;
      const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (ts && Date.now() - ts < 7 * 24 * 60 * 60 * 1000) dismissedRef.current = true;
    } catch {}
  }

  useEffect(() => {
    if (typeof window === "undefined" || dismissedRef.current) return;

    let armed = false;
    let scrollArmed = false;
    const tryOpen = () => {
      if (dismissedRef.current) return;
      setOpen(true);
    };

    const armTimer = setTimeout(() => { armed = true; }, 10000);
    const mobileTimer = setTimeout(() => {
      if (window.innerWidth < 1024) tryOpen();
    }, 30000);
    const scrollArmTimer = setTimeout(() => { scrollArmed = true; }, 30000);

    const onLeave = (e) => {
      if (!armed || dismissedRef.current) return;
      if (e.clientY <= 0 && window.innerWidth >= 1024) tryOpen();
    };
    const onScroll = () => {
      if (!scrollArmed || dismissedRef.current) return;
      const h = document.documentElement;
      const ratio = (h.scrollTop + window.innerHeight) / h.scrollHeight;
      if (ratio >= 0.5) tryOpen();
    };

    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(armTimer);
      clearTimeout(mobileTimer);
      clearTimeout(scrollArmTimer);
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const dismiss = () => {
    dismissedRef.current = true;
    setOpen(false);
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
  };

  if (!open || dismissedRef.current) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4"
      data-testid="exit-intent-overlay"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-popup"
      >
        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-600 hover:text-slate-900 grid place-items-center z-10 shadow"
          data-testid="exit-intent-close"
        >
          <X size={16} />
        </button>
        <div className="px-6 pt-6 pb-2 bg-[#EA580C] text-white">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-100">Limited offer</span>
          <h3 className="text-2xl font-semibold mt-1 leading-tight" style={{ fontFamily: "Outfit" }}>
            Wait! Get ₹15,000 off your Digital Board
          </h3>
          <p className="text-amber-100 text-sm mt-1">Plus free on-site demo + ₹0 EMI starting today.</p>
        </div>
        <div className="p-5">
          <LeadForm
            sourcePage="exit_intent"
            variant="plain"
            title=""
            subtitle=""
            testIdPrefix="exit-intent-form"
            onSuccess={dismiss}
          />
        </div>
      </div>
    </div>
  );
}
